import { useReactFlow, Handle, Position, NodeResizer } from "@xyflow/react";
import { Input, Button, Select, Radio } from "antd";
import { PlusOutlined, DeleteOutlined, InteractionOutlined, CloseOutlined, CopyOutlined } from "@ant-design/icons";
import NodeNameInput from "./NodeNameInput";

const { Option } = Select;

const InteractiveButtonsNode = ({ data, selected, id }) => {
  const { deleteElements } = useReactFlow();
  const emit = (patch) => data.onChange?.({ ...data, ...patch });

  const isCta = data.buttonType === "cta_url";

  const addButton = () => {
    const buttons = [...(data.buttons || [])];
    if (buttons.length >= 3) return;
    emit({ buttons: [...buttons, { id: `btn_${Date.now()}`, label: "" }] });
  };

  const updateButton = (index, field, value) => {
    emit({
      buttons: (data.buttons || []).map((b, i) => i === index ? { ...b, [field]: value } : b),
    });
  };

  const removeButton = (index) => {
    emit({ buttons: (data.buttons || []).filter((_, i) => i !== index) });
  };

  return (
    <div
      style={{
        background: "#fff",
        border: `2px solid ${selected ? "#8b5cf6" : "#e5e7eb"}`,
        borderRadius: 10,
        padding: 14,
        minWidth: 300,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
      }}
    >
      <NodeResizer minWidth={240} minHeight={80} isVisible={selected} color="#8b5cf6" />
      <Handle type="target" position={Position.Left} style={{ background: "#8b5cf6" }} />

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <InteractionOutlined style={{ color: "#8b5cf6", fontSize: 16 }} />
        <strong style={{ fontSize: 13 }}>Interactive Buttons</strong>
        <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => data.onDuplicate?.()} style={{ marginLeft: "auto", color: "#9ca3af" }} />
        <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => deleteElements({ nodes: [{ id }] })} style={{ color: "#9ca3af" }} />
      </div>

      <div
        className="nodrag"
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
      <NodeNameInput data={data} />

      {/* Header */}
      <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>
        Header (Optional)
      </label>
      <div>
        <Select
          value={data.headerType || "none"}
          onChange={(v) => emit({ headerType: v })}
          size="small"
          style={{ width: "100%", marginBottom: data.headerType === "text" ? 4 : 8 }}
        >
          <Option value="none">None</Option>
          <Option value="text">Text</Option>
        </Select>
      </div>
      {data.headerType === "text" && (
        <Input
          value={data.headerText || ""}
          onChange={(e) => emit({ headerText: e.target.value })}
          placeholder="Header text"
          size="small"
          style={{ marginBottom: 8 }}
        />
      )}

      {/* Body */}
      <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>
        Body <span style={{ color: "#ef4444" }}>*</span>
      </label>
      <Input.TextArea
        value={data.body || ""}
        onChange={(e) => emit({ body: e.target.value })}
        rows={2}
        placeholder="Enter the main message for this message type"
        style={{ fontSize: 12, marginBottom: 8 }}
      />

      {/* Footer */}
      <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>
        Footer Text (Optional)
      </label>
      <Input
        value={data.footer || ""}
        onChange={(e) => emit({ footer: e.target.value })}
        placeholder="Enter footer text"
        size="small"
        maxLength={60}
        style={{ marginBottom: 10 }}
      />

      {/* Button type toggle */}
      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 6 }}>
          Button type:
        </label>
        <Radio.Group
          value={data.buttonType || "reply"}
          onChange={(e) => emit({ buttonType: e.target.value })}
          size="small"
        >
          <Radio value="reply">Reply Buttons</Radio>
          <Radio value="cta_url">CTA URL Button</Radio>
        </Radio.Group>
      </div>

      {isCta ? (
        /* CTA URL mode */
        <div style={{ position: "relative" }}>
          <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>
            Button URL
          </label>
          <Input
            value={data.ctaUrl || ""}
            onChange={(e) => emit({ ctaUrl: e.target.value })}
            placeholder="https://..."
            size="small"
            style={{ marginBottom: 8 }}
          />
          <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>
            Button Label{" "}
            <span style={{ color: "#9ca3af" }}>{(data.ctaLabel || "").length}/20</span>
          </label>
          <Input
            value={data.ctaLabel || ""}
            onChange={(e) => emit({ ctaLabel: e.target.value })}
            placeholder="e.g. Learn More"
            size="small"
            maxLength={20}
          />
          <Handle
            type="source"
            position={Position.Right}
            id="cta"
            style={{ background: "#8b5cf6" }}
          />
        </div>
      ) : (
        /* Reply Buttons mode */
        <>
          <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 6 }}>
            Reply Buttons (atleast 1 button)
          </div>
          {(data.buttons || []).map((btn, i) => (
            <div
              key={btn.id}
              style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, position: "relative" }}
            >
              <Input
                value={btn.label}
                onChange={(e) => updateButton(i, "label", e.target.value)}
                placeholder={`Button ${i + 1} Label`}
                size="small"
                maxLength={20}
                style={{ flex: 1 }}
              />
              <span style={{ fontSize: 10, color: "#9ca3af", minWidth: 32 }}>
                {btn.label.length}/20
              </span>
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeButton(i)}
              />
              <Handle
                type="source"
                position={Position.Right}
                id={btn.id}
                style={{ background: "#8b5cf6", top: "50%", right: -14 }}
              />
            </div>
          ))}
          {(data.buttons || []).length < 3 && (
            <Button size="small" icon={<PlusOutlined />} onClick={addButton} style={{ marginTop: 4 }}>
              Add Button
            </Button>
          )}
        </>
      )}
      </div>{/* end nodrag wrapper */}
    </div>
  );
};

export default InteractiveButtonsNode;
